import storeLang from '../../helpers/storeLang';
import {
	GreenSidely,
	GreySidely,
	RedSidely
} from '../../styles/global/css/Utils';

export const currencies = [
	['EUR', '€'],
	['USD', '$'],
	['JPY', '¥'],
	['GBP', '£'],
	['CHF', 'Fr.'],
	['SEK', 'kr'],
	['KRW', '₩'],
	['CAD', '$'],
	['AUD', '$'],
];

export function getLang() {
	const active_lang = storeLang
		.getState()
		.localize.languages.filter(l => l.active);
	if (active_lang.length > 0) {
		if (active_lang[0].code == 'en') {
			return 'en-GB';
		} else if (active_lang[0].code == 'fr') {
			return 'fr-FR';
		}
	}
	return 'en-GB';
}

function checkCurrencyCode(currencyCode: string): string {
	for (let i = 0; i < currencies.length; i++) {
		if (currencies[i][0] == currencyCode) {
			return currencyCode;
		}
	}
	return 'EUR';
}

export function getNumberFormatter(
	options?: Intl.NumberFormatOptions | undefined
): Intl.NumberFormat {
	const userLocale = getLanguage();
	return new Intl.NumberFormat(userLocale, options);
}

export function getCurrencyFormatter(): Intl.NumberFormat {
	let currency = localStorage.getItem('currency') || 'EUR';
	currency = checkCurrencyCode(currency);
	return getNumberFormatter({
		style: 'currency',
		currency
	});
}

export function formatCurrency(amount: number | null): string {
	if (amount === null || amount === undefined) {
		return '-';
	}
	return getCurrencyFormatter().format(amount);
}

export function getCurrencySymbol(): string {
	const userLocale = getLanguage();
	let currency = localStorage.getItem('currency') || 'EUR';
	currency = checkCurrencyCode(currency);
	return (0)
		.toLocaleString(userLocale, {
			style: 'currency',
			currency,
			minimumFractionDigits: 0,
			maximumFractionDigits: 0
		})
		.replace(/\d/g, '')
		.trim();
}

export function queryConstructor(state, date, additional_object?: object) {
	const { userId, tags, products, userIdList, timeRange, brandList } = state;
	let userIdListQuery = '';
	if (userIdList && userIdList.some(e => Array.isArray(e))) {
		if (userIdList[0] !== undefined) {
			userIdListQuery += userIdList[0].length
				? userIdList[0]
					.reduce((acc, x) => {
						return acc + x + ',';
					}, 'company_owners=[')
					.slice(0, -1) + ']&'
				: 'company_owners=[]&';
		}
		if (userIdList[1] !== undefined) {
			userIdListQuery += userIdList[1].length
				? userIdList[1]
					.reduce((acc, x) => {
						return acc + x + ',';
					}, 'users=[')
					.slice(0, -1) + ']&'
				: 'users=[]&';
		}
	} else if (userIdList && userIdList.length) {
		userIdListQuery =
      userIdList
      	.reduce((acc, x) => {
      		return acc + x + ',';
      	}, 'company_owners=[')
      	.slice(0, -1) + ']&';
	}
	let ret =
    '?' +
    (userId ? 'id=' + userId + '&' : '') +
    (date ? 'date=' + date + '&' : '') +
    (brandList ? `brands=[${brandList.map(b => b.id).join(',')}]&` : '') +
    (timeRange ? 'time_range=' + timeRange + '&' : '') +
    (tags && tags.length
    	? tags
    		.reduce((acc, x) => {
    			return acc + x + '-';
    		}, 'tags=')
    		.slice(0, -1) + '&'
    	: '') +
    (products != null
    	? products
    		.reduce((acc, x) => {
    			return acc + x + '-';
    		}, 'products=')
    		.slice(0, -1) + '&'
    	: '') +
    userIdListQuery;
	if (additional_object)
		Object.entries(additional_object).forEach(
			([key, value]) => (ret = `${ret}${key}=${value}&`)
		);
	return ret.slice(0, -1);
}

type PercentageColor = {
  pct: number;
  color: {
    r: number;
    g: number;
    b: number;
  };
};

const defaultPercentColors = [
	{ pct: 0.0, color: { r: 0xff, g: 0x76, b: 0x69 } },
	{ pct: 0.65, color: { r: 0xff, g: 0xea, b: 0x2c } },
	{ pct: 1.0, color: { r: 0x2e, g: 0xd4, b: 0x7a } }
];

export function getColorForEvolution(dnEvolution: number | null) {
	if (dnEvolution && dnEvolution > 0) return GreenSidely;
	if (dnEvolution && dnEvolution < 0) return RedSidely;
	return GreySidely;
}

export function getColorForPercentage(
	pct: number,
	alpha = 1.0,
	percentColors: PercentageColor[] = defaultPercentColors,
	isScale01?: boolean
) {
	if (isScale01 === true && pct > 1) return `rgb(43, 170, 255, ${alpha})`;
	if (pct > 100) pct = 100;
	let i = 1;
	for (i; i < percentColors.length - 1; i++) {
		if (pct < percentColors[i].pct) {
			break;
		}
	}
	const lower = percentColors[i - 1];
	const upper = percentColors[i];
	const range = upper.pct - lower.pct;
	const rangePct = (pct - lower.pct) / range;
	const pctLower = 1 - rangePct;
	const pctUpper = rangePct;
	const color = {
		r: Math.floor(lower.color.r * pctLower + upper.color.r * pctUpper),
		g: Math.floor(lower.color.g * pctLower + upper.color.g * pctUpper),
		b: Math.floor(lower.color.b * pctLower + upper.color.b * pctUpper)
	};
	return 'rgb(' + [color.r, color.g, color.b].join(',') + ',' + alpha + ')';
}

export const getStringWidth = (
	s: string,
	maxWidth = 400,
	minWidth = 120,
	magicSpacing = 8
) => {
	const sLength = Math.min(maxWidth, s.length * magicSpacing);
	return Math.min(Math.max(minWidth, sLength), maxWidth);
};

export function getLanguage(): string {
	const lang = localStorage.getItem('language') || navigator.language;
	if (!lang) return 'en-GB';
	switch (lang.split('-')[0]) {
		case 'fr':
		case 'fr-FR':
			return 'fr-FR';
		case 'de':
		case 'de-DE':
			return 'de-DE';
		default:
			return 'en-GB';
	}
}

export function getLanguageCode(): string {
	return getLanguage().split('-')[0];
}
